import { useEffect, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ApiError } from '@/api/client'
import { mailApi } from '@/api/endpoints'
import { Button } from '@/components/ui/Button'
import { Skeleton } from '@/components/ui/Feedback'
import { StatusPill } from '@/components/ui/StatusPill'
import { Switch } from '@/components/ui/Toggle'
import { aliasReady, copyText, retryUnlessMissing, useMailAlias } from '@/features/mail/mailMeta'
import { cn, relativeTime } from '@/lib/utils'
import { toast } from '@/store/toast'
import type { MailAccount, MailAlias, MailAliasUpdate, MailProvider } from '@/types/api'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// ---------------------------------------------------------------------------
// Shared rows: one setting, with its control
// ---------------------------------------------------------------------------
/**
 * One setting: label and help on the left, the control on the right. On a
 * phone the control drops under the label and takes the full width.
 */
export function SettingRow({
  label,
  hint,
  children,
  htmlFor,
  className,
}: {
  label: ReactNode
  hint?: ReactNode
  children: ReactNode
  /** When the control is an input: the row's label points at it. */
  htmlFor?: string
  className?: string
}) {
  const Label = htmlFor ? 'label' : 'p'
  return (
    <div className={cn('flex flex-col gap-2.5 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6', className)}>
      <div className="min-w-0">
        <Label htmlFor={htmlFor} className="text-[15px] font-medium text-ink">
          {label}
        </Label>
        {hint && <p className="mt-0.5 text-[13px] leading-relaxed text-ink-muted">{hint}</p>}
      </div>
      <div className="flex shrink-0 items-center sm:justify-end sm:min-w-[120px]">{children}</div>
    </div>
  )
}

/** An address in mono with a copy button beside it. */
export function AddressPill({ address, className }: { address: string; className?: string }) {
  return (
    <span className={cn('inline-flex max-w-full items-stretch overflow-hidden rounded-field border border-line bg-surface-muted', className)}>
      <span className="num min-w-0 truncate px-3 py-2 text-[13px] text-ink">{address}</span>
      <button
        type="button"
        onClick={async () => {
          if (await copyText(address)) toast.success('Address copied')
          else toast.error('Could not copy')
        }}
        aria-label={`Copy ${address}`}
        title="Copy"
        className="grid w-11 shrink-0 place-items-center border-l border-line text-ink-muted transition-colors hover:bg-surface hover:text-ink"
      >
        <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <rect x="5.5" y="5.5" width="8" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
          <path d="M10.5 5.5v-2a1 1 0 0 0-1-1h-6a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2" stroke="currentColor" strokeWidth="1.3" />
        </svg>
      </button>
    </span>
  )
}

// ---------------------------------------------------------------------------
// Application email (the alias)
// ---------------------------------------------------------------------------
export function ApplicationEmailSettings() {
  const queryClient = useQueryClient()
  const aliasQuery = useMailAlias()
  const alias = aliasQuery.data

  const update = useMutation({
    mutationFn: (patch: MailAliasUpdate) => mailApi.updateAlias(patch),
    onMutate: (patch) => {
      const previous = queryClient.getQueryData<MailAlias>(['mail-alias'])
      if (previous) queryClient.setQueryData<MailAlias>(['mail-alias'], { ...previous, ...patch })
      return { previous }
    },
    onSuccess: (updated) => {
      if (updated && typeof updated === 'object') queryClient.setQueryData(['mail-alias'], updated)
      toast.success('Settings saved')
    },
    onError: (error: ApiError, _patch, context) => {
      if (context?.previous) queryClient.setQueryData(['mail-alias'], context.previous)
      toast.error('Could not save', error.message)
    },
  })

  if (aliasQuery.isLoading) {
    return (
      <div className="space-y-3 py-4">
        <Skeleton className="h-9 w-72 max-w-full" />
        <Skeleton className="h-4 w-96 max-w-full" />
      </div>
    )
  }

  if (!alias) {
    return (
      <div className="py-4">
        <p className="flex flex-wrap items-center gap-2 text-[15px] font-medium text-ink">
          No address yet <StatusPill size="sm">Not configured</StatusPill>
        </p>
        <p className="mt-0.5 text-[13px] leading-relaxed text-ink-muted">
          Your application email isn't available yet. Until it is, we apply with your own address.
        </p>
      </div>
    )
  }

  const ready = aliasReady(alias)
  const active = ready && alias.enabled

  return (
    <>
      <div className="py-4">
        <div className="flex flex-wrap items-center gap-2">
          {alias.address ? (
            <AddressPill address={alias.address} />
          ) : (
            <p className="text-[15px] font-medium text-ink">No address yet</p>
          )}
          {active ? (
            <StatusPill tone="success">Active</StatusPill>
          ) : ready ? (
            <StatusPill>Paused</StatusPill>
          ) : (
            <StatusPill tone="warning">Not configured</StatusPill>
          )}
        </div>
        <p className="mt-2 text-[13px] leading-relaxed text-ink-muted">
          {ready
            ? 'We create employer accounts with this address, use the codes and links they send automatically, and file every reply in your Inbox.'
            : "Your administrator hasn't configured the mail domain yet. Until then we apply with your own email address."}
        </p>
      </div>

      <SettingRow label="Use this address when applying" hint="Employers see this address instead of your personal one.">
        <Switch
          checked={alias.enabled}
          disabled={!ready}
          onChange={(value) => update.mutate({ enabled: value })}
          label="Use this address when applying"
        />
      </SettingRow>

      <div>
        <SettingRow
          label="Forward important emails"
          hint="Interview invites, assessments and offers are sent on to you. Codes and receipts stay here."
          className={alias.forwardingEnabled ? 'pb-0' : undefined}
        >
          <Switch
            checked={alias.forwardingEnabled}
            disabled={!ready}
            onChange={(value) => update.mutate({ forwardingEnabled: value })}
            label="Forward important emails"
          />
        </SettingRow>
        {alias.forwardingEnabled && (
          <ForwardToField
            value={alias.forwardTo ?? ''}
            disabled={!ready}
            onSave={(forwardTo) => update.mutate({ forwardTo })}
          />
        )}
      </div>
    </>
  )
}

function ForwardToField({
  value,
  disabled,
  onSave,
}: {
  value: string
  disabled: boolean
  onSave: (value: string) => void
}) {
  const [draft, setDraft] = useState(value)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => setDraft(value), [value])

  const commit = () => {
    const next = draft.trim()
    if (next === value) return setError(null)
    if (!EMAIL_RE.test(next)) return setError('Enter a valid email address')
    setError(null)
    onSave(next)
  }

  return (
    <div className="max-w-[420px] pb-4 pt-3">
      <label htmlFor="forward-to" className="label-eyebrow mb-2 block">
        Forward to
      </label>
      <input
        id="forward-to"
        type="email"
        value={draft}
        disabled={disabled}
        placeholder="you@example.com"
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => event.key === 'Enter' && (event.currentTarget as HTMLInputElement).blur()}
        aria-invalid={error ? true : undefined}
        className="field-box h-11 w-full sm:h-10"
      />
      {error && <p className="mt-1.5 text-[13px] text-danger">{error}</p>}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Connected mailboxes (Gmail / Outlook)
// ---------------------------------------------------------------------------
const PROVIDERS: Array<{ value: MailProvider; label: string }> = [
  { value: 'gmail', label: 'Gmail' },
  { value: 'outlook', label: 'Outlook' },
]

export function EmailIntegrationsSettings() {
  const queryClient = useQueryClient()
  const accountsQuery = useQuery({
    queryKey: ['mail-accounts'],
    queryFn: mailApi.accounts,
    retry: retryUnlessMissing,
  })
  const accounts: MailAccount[] = Array.isArray(accountsQuery.data) ? accountsQuery.data : []
  const missing = accountsQuery.error instanceof ApiError && accountsQuery.error.status === 404

  const connect = useMutation({
    mutationFn: (provider: MailProvider) => mailApi.connect(provider),
    onSuccess: (result, provider) => {
      if (result?.authUrl) window.location.href = result.authUrl
      else toast.error(`Could not connect ${providerLabel(provider)}`, 'No sign-in link came back.')
    },
    onError: (error: ApiError, provider) =>
      toast.error(
        `Could not connect ${providerLabel(provider)}`,
        error.status === 404 ? 'This integration is not available yet.' : error.message,
      ),
  })

  const sync = useMutation({
    mutationFn: (id: string) => mailApi.sync(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mail-accounts'] })
      queryClient.invalidateQueries({ queryKey: ['mail-messages'] })
      toast.success('Sync started')
    },
    onError: (error: ApiError) => toast.error('Sync failed', error.message),
  })

  const disconnect = useMutation({
    mutationFn: (id: string) => mailApi.disconnect(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mail-accounts'] })
      toast.info('Mailbox disconnected')
    },
    onError: (error: ApiError) => toast.error('Could not disconnect', error.message),
  })

  return (
    <>
      <div className="py-4">
        <p className="text-[13px] leading-relaxed text-ink-muted">
          Already applied with your own address? Connect it and we'll sort replies from employers into your Inbox.
          We only read mail about your applications.
        </p>
        <div className="mt-3 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
          {PROVIDERS.map((provider) => (
            <Button
              key={provider.value}
              variant="secondary"
              size="sm"
              icon={<ProviderMark provider={provider.value} size="sm" />}
              loading={connect.isPending && connect.variables === provider.value}
              onClick={() => connect.mutate(provider.value)}
              className="h-11 sm:h-9"
            >
              Connect {provider.label}
            </Button>
          ))}
        </div>
      </div>

      {accountsQuery.isLoading ? (
        <div className="py-4">
          <Skeleton className="h-10 w-full" />
        </div>
      ) : accounts.length > 0 ? (
        accounts.map((account) => (
          <AccountRow
            key={account.id}
            account={account}
            syncing={sync.isPending && sync.variables === account.id}
            disconnecting={disconnect.isPending && disconnect.variables === account.id}
            onSync={() => sync.mutate(account.id)}
            onDisconnect={() => disconnect.mutate(account.id)}
            onReconnect={() => connect.mutate(account.provider)}
          />
        ))
      ) : (
        <p className="py-4 text-[13px] text-ink-faint">
          {accountsQuery.isError && !missing ? 'Could not load connected mailboxes.' : 'No mailboxes connected.'}
        </p>
      )}
    </>
  )
}

function providerLabel(provider: string) {
  return provider === 'outlook' ? 'Outlook' : provider === 'gmail' ? 'Gmail' : provider
}

function AccountRow({
  account,
  syncing,
  disconnecting,
  onSync,
  onDisconnect,
  onReconnect,
}: {
  account: MailAccount
  syncing: boolean
  disconnecting: boolean
  onSync: () => void
  onDisconnect: () => void
  onReconnect: () => void
}) {
  const connected = account.status === 'connected'
  return (
    <div className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
      <div className="flex min-w-0 items-start gap-3">
        <ProviderMark provider={account.provider} />
        <div className="min-w-0">
          <p className="flex flex-wrap items-center gap-2 text-[15px] font-medium text-ink">
            <span className="num break-all text-[14px]">{account.email}</span>
            {connected ? (
              <StatusPill size="sm" tone="success">
                Connected
              </StatusPill>
            ) : account.status === 'revoked' ? (
              <StatusPill size="sm" tone="warning">
                Access revoked
              </StatusPill>
            ) : (
              <StatusPill size="sm" tone="danger">
                Error
              </StatusPill>
            )}
          </p>
          <p className="mt-0.5 break-words text-[13px] text-ink-muted">
            {!connected && account.lastError
              ? account.lastError
              : `${providerLabel(account.provider)} · last synced ${relativeTime(account.lastSyncedAt)}`}
          </p>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2 pl-[52px] sm:pl-0">
        {connected ? (
          <Button size="sm" variant="secondary" onClick={onSync} loading={syncing}>
            Sync now
          </Button>
        ) : (
          <Button size="sm" onClick={onReconnect}>
            Reconnect
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={onDisconnect} loading={disconnecting}>
          Disconnect
        </Button>
      </div>
    </div>
  )
}

/** A letter tile for a mail provider: the same mark whatever the theme. */
export function ProviderMark({ provider, size = 'md' }: { provider: string; size?: 'sm' | 'md' }) {
  const letter = provider === 'outlook' ? 'O' : provider === 'gmail' ? 'G' : provider.slice(0, 1).toUpperCase()
  return (
    <span
      aria-hidden
      className={cn(
        'grid shrink-0 place-items-center rounded-[9px] border border-line bg-surface-muted font-semibold text-ink-soft',
        size === 'sm' ? 'h-5 w-5 rounded-[6px] text-[11px]' : 'h-10 w-10 text-[14px]',
      )}
    >
      {letter}
    </span>
  )
}
